import type { AppRouter } from '@stopal/api'
import {
  CLIENT_MODEL_REQUEST_TIMEOUT_MS,
  CLIENT_REQUEST_TIMEOUT_MS,
  isModelBackedProcedure,
} from '@stopal/shared/request-timeouts'
import type { QueryClient } from '@tanstack/react-query'
import { createTRPCClient, httpBatchLink, splitLink } from '@trpc/client'
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import superjson from 'superjson'
import { fetchWithTimeout } from './fetch-with-timeout'
import { makeQueryClient } from './query-client'

let clientQueryClient: QueryClient | undefined

export function getClientQueryClient() {
  if (typeof window === 'undefined') return makeQueryClient()
  if (!clientQueryClient) clientQueryClient = makeQueryClient()
  return clientQueryClient
}

const batchOptions = { url: '/api/trpc', transformer: superjson } as const

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    // Two links rather than one wrapper reading the path, because the split
    // also separates the BATCHES. `httpBatchLink` merges by time window, so one
    // link would put a model call and an ordinary query in a single request
    // under a single deadline — wrong for whichever half did not choose it.
    //
    // It does not make a model call standalone: members of this link batch
    // with each other, within an operation type (`httpBatchLink` keeps one
    // loader per type, so the two queries never join the three mutations).
    // Nothing here relies on that — the AI procedures' per-path rate-limit
    // mounts were retired for middleware precisely because "never batched" was
    // a claim about the client.
    splitLink({
      condition: (op) => isModelBackedProcedure(op.path),
      true: httpBatchLink({
        ...batchOptions,
        fetch: fetchWithTimeout(CLIENT_MODEL_REQUEST_TIMEOUT_MS),
      }),
      false: httpBatchLink({
        ...batchOptions,
        fetch: fetchWithTimeout(CLIENT_REQUEST_TIMEOUT_MS),
      }),
    }),
  ],
})

export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient: getClientQueryClient,
})
